import { supabase } from './supabase';
import type { AnalyticsData } from '../types';

/**
 * Service for calculating workout analytics and progress tracking
 * Implements requirements 8.1, 8.2, 8.3, 8.4
 */
export class AnalyticsService {
  /**
   * Check if running in development mode
   */
  private isDevelopmentMode(): boolean {
    return import.meta.env.VITE_DEV_MODE === 'true';
  }

  /**
   * Calculate analytics for a specific exercise
   * Requirements: 8.1, 8.2
   */
  async getExerciseAnalytics(exerciseId: string, userId: string): Promise<AnalyticsData> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: returning mock exercise analytics');
      
      return {
        exercise_id: exerciseId,
        exercise_name: 'Жим лежа',
        personal_best: {
          weight: 100,
          reps: 5,
          date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
        },
        weight_progression: [
          {
            date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            max_weight: 90,
            total_volume: 2700 // 90kg * 30 reps
          },
          {
            date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
            max_weight: 95,
            total_volume: 2850
          },
          {
            date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            max_weight: 100,
            total_volume: 3000
          }
        ],
        total_sessions: 12,
        average_volume: 2850
      };
    }

    // Real implementation for database
    try {
      // Get exercise name
      const { data: exercise } = await supabase
        .from('exercise_catalog')
        .select('name_ru')
        .eq('id', exerciseId)
        .single();

      if (!exercise) {
        throw new Error('Exercise not found');
      }

      // Get all sets for this exercise by this user
      const { data: sets, error } = await supabase
        .from('sets')
        .select(`
          weight,
          reps,
          created_at,
          workouts!inner(user_id, completed_at)
        `)
        .eq('exercise_id', exerciseId)
        .eq('workouts.user_id', userId)
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      if (!sets || sets.length === 0) {
        return {
          exercise_id: exerciseId,
          exercise_name: (exercise as any).name_ru,
          personal_best: { weight: 0, reps: 0, date: new Date().toISOString() },
          weight_progression: [],
          total_sessions: 0,
          average_volume: 0
        };
      }

      // Calculate personal best (highest weight * reps)
      let personalBest = { weight: 0, reps: 0, date: new Date().toISOString() };
      let maxScore = 0;

      sets.forEach((set: any) => {
        const score = set.weight * set.reps;
        if (score > maxScore) {
          maxScore = score;
          personalBest = {
            weight: set.weight,
            reps: set.reps,
            date: set.created_at
          };
        }
      });

      // Group by workout sessions and calculate progression
      const sessionMap = new Map();
      sets.forEach((set: any) => {
        const sessionDate = new Date(set.workouts.completed_at).toDateString();
        if (!sessionMap.has(sessionDate)) {
          sessionMap.set(sessionDate, {
            date: set.workouts.completed_at,
            sets: []
          });
        }
        sessionMap.get(sessionDate).sets.push(set);
      });

      const weightProgression = Array.from(sessionMap.values()).map(session => {
        const maxWeight = Math.max(...session.sets.map((s: any) => s.weight));
        const totalVolume = session.sets.reduce((sum: number, s: any) => sum + (s.weight * s.reps), 0);
        
        return {
          date: session.date,
          max_weight: maxWeight,
          total_volume: totalVolume
        };
      });

      const totalSessions = sessionMap.size;
      const averageVolume = weightProgression.reduce((sum, p) => sum + p.total_volume, 0) / totalSessions;

      return {
        exercise_id: exerciseId,
        exercise_name: (exercise as any).name_ru,
        personal_best: personalBest,
        weight_progression: weightProgression,
        total_sessions: totalSessions,
        average_volume: Math.round(averageVolume)
      };

    } catch (error) {
      console.error('Error calculating exercise analytics:', error);
      throw new Error('Ошибка при расчете аналитики упражнения');
    }
  }

  /**
   * Get overall workout statistics for user
   * Requirements: 8.3, 8.4
   */
  async getOverallStats(userId: string): Promise<{
    total_workouts: number;
    total_volume: number;
    average_workout_duration: number;
    workouts_this_month: number;
    favorite_exercises: Array<{ exercise_name: string; sessions: number }>;
    strength_progress: number; // percentage increase in average weight
  }> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: returning mock overall stats');
      
      return {
        total_workouts: 24,
        total_volume: 68400, // kg
        average_workout_duration: 52, // minutes
        workouts_this_month: 8,
        favorite_exercises: [
          { exercise_name: 'Жим лежа', sessions: 12 },
          { exercise_name: 'Приседания', sessions: 10 },
          { exercise_name: 'Становая тяга', sessions: 8 }
        ],
        strength_progress: 15.5 // 15.5% increase
      };
    }

    // Real implementation for database
    try {
      // Get total workouts
      const { count: totalWorkouts } = await supabase
        .from('workouts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .not('completed_at', 'is', null);

      // Get workouts this month
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const { count: workoutsThisMonth } = await supabase
        .from('workouts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('completed_at', monthStart.toISOString())
        .not('completed_at', 'is', null);

      // Calculate total volume and average duration
      const { data: workoutData } = await supabase
        .from('workouts')
        .select(`
          started_at,
          completed_at,
          sets!inner(weight, reps)
        `)
        .eq('user_id', userId)
        .not('completed_at', 'is', null);

      let totalVolume = 0;
      let totalDuration = 0;

      if (workoutData) {
        workoutData.forEach((workout: any) => {
          // Calculate volume for this workout
          const workoutVolume = workout.sets.reduce((sum: number, set: any) => 
            sum + (set.weight * set.reps), 0);
          totalVolume += workoutVolume;

          // Calculate duration
          if (workout.started_at && workout.completed_at) {
            const duration = new Date(workout.completed_at).getTime() - new Date(workout.started_at).getTime();
            totalDuration += duration / (1000 * 60); // Convert to minutes
          }
        });
      }

      const averageWorkoutDuration = totalWorkouts ? Math.round(totalDuration / totalWorkouts) : 0;

      // Get favorite exercises (most frequently used)
      const { data: exerciseStats } = await supabase
        .from('sets')
        .select(`
          exercise_id,
          exercise_catalog!inner(name_ru),
          workouts!inner(user_id)
        `)
        .eq('workouts.user_id', userId);

      const exerciseFrequency = new Map();
      if (exerciseStats) {
        exerciseStats.forEach((stat: any) => {
          const exerciseName = stat.exercise_catalog.name_ru;
          exerciseFrequency.set(exerciseName, (exerciseFrequency.get(exerciseName) || 0) + 1);
        });
      }

      const favoriteExercises = Array.from(exerciseFrequency.entries())
        .map(([exercise_name, sessions]) => ({ exercise_name, sessions }))
        .sort((a, b) => b.sessions - a.sessions)
        .slice(0, 5);

      // Calculate strength progress (simplified - compare first month vs last month)
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      const { data: recentSets } = await supabase
        .from('sets')
        .select('weight, workouts!inner(user_id, completed_at)')
        .eq('workouts.user_id', userId)
        .gte('workouts.completed_at', monthStart.toISOString());

      const { data: oldSets } = await supabase
        .from('sets')
        .select('weight, workouts!inner(user_id, completed_at)')
        .eq('workouts.user_id', userId)
        .lt('workouts.completed_at', threeMonthsAgo.toISOString());

      let strengthProgress = 0;
      if (recentSets && oldSets && recentSets.length > 0 && oldSets.length > 0) {
        const recentAvg = recentSets.reduce((sum: number, set: any) => sum + set.weight, 0) / recentSets.length;
        const oldAvg = oldSets.reduce((sum: number, set: any) => sum + set.weight, 0) / oldSets.length;
        strengthProgress = oldAvg > 0 ? ((recentAvg - oldAvg) / oldAvg) * 100 : 0;
      }

      return {
        total_workouts: totalWorkouts || 0,
        total_volume: Math.round(totalVolume),
        average_workout_duration: averageWorkoutDuration,
        workouts_this_month: workoutsThisMonth || 0,
        favorite_exercises: favoriteExercises,
        strength_progress: Math.round(strengthProgress * 10) / 10
      };

    } catch (error) {
      console.error('Error calculating overall stats:', error);
      throw new Error('Ошибка при расчете общей статистики');
    }
  }

  /**
   * Get workout comparison data
   * Requirements: 8.4
   */
  async compareWorkouts(workoutId1: string, workoutId2: string): Promise<{
    workout1: { date: string; total_volume: number; duration: number; exercises: number };
    workout2: { date: string; total_volume: number; duration: number; exercises: number };
    volume_difference: number;
    duration_difference: number;
  }> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: returning mock workout comparison');
      
      return {
        workout1: {
          date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          total_volume: 3200,
          duration: 45,
          exercises: 5
        },
        workout2: {
          date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
          total_volume: 2950,
          duration: 52,
          exercises: 4
        },
        volume_difference: 8.5, // 8.5% increase
        duration_difference: -13.5 // 13.5% decrease (faster)
      };
    }

    // Real implementation would compare actual workout data
    throw new Error('Workout comparison not implemented for real database yet');
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();